/**
 * Конкурсы: идущие и завершённые.
 *
 * Билеты начисляются за оплату подписки сами — здесь только посмотреть,
 * сколько их и где.
 */
import { useNavigate } from 'react-router';

import { drawText, useRaffles } from '@/features/raffle/raffles';
import type { Raffle } from '@/shared/api/schemas';
import { useBackButton } from '@/shared/lib/useBackButton';
import { ticketWord } from '@/shared/lib/tickets';
import { Screen } from '@/shared/ui/Screen';
import { GroupCaption } from '@/shared/ui/kit/Card';

import styles from './Raffle.module.css';

const number = new Intl.NumberFormat('ru-RU');

function RaffleCard({ raffle, onOpen }: { raffle: Raffle; onOpen: () => void }) {
  return (
    <button type="button" className={styles.card} onClick={onOpen}>
      {raffle.image_url ? (
        <img className={styles.image} src={raffle.image_url} alt="" loading="lazy" referrerPolicy="no-referrer" />
      ) : null}
      <span className={styles.cardBody}>
        <span className={styles.chip}>{drawText(raffle)}</span>
        <span className={styles.title}>{raffle.title}</span>
        <span className={styles.meta}>
          <span className={styles.muted}>Ваши билеты</span>
          <span className={raffle.my_tickets ? styles.mine : undefined}>{number.format(raffle.my_tickets)}</span>
        </span>
        <span className={styles.note}>Не получится отправить приз — выплатим его стоимость.</span>
      </span>
    </button>
  );
}

export function RafflesPage() {
  const navigate = useNavigate();
  useBackButton(() => void navigate('/profile'));
  const { data, isLoading, error, refetch } = useRaffles();
  const items = data?.items ?? [];
  const active = items.filter((r) => r.status === 'active');
  const drawn = items.filter((r) => r.status === 'drawn');
  const mine = active.reduce((sum, r) => sum + r.my_tickets, 0);

  return (
    <Screen isLoading={isLoading} error={error} onRetry={() => void refetch()}>
      <h1 className={styles.head}>Конкурсы</h1>
      <p className={styles.intro}>
        {mine > 0
          ? `У вас ${number.format(mine)} ${ticketWord(mine)} в идущих конкурсах.`
          : 'Билеты начисляются за каждую оплату подписки: 1 месяц — 1 билет, 1 год — 12 билетов.'}
      </p>

      {items.length === 0 && !isLoading ? <p className={styles.intro}>Сейчас конкурсов нет — загляните позже.</p> : null}

      {active.length > 0 ? <GroupCaption>Идут</GroupCaption> : null}
      {active.map((raffle) => (
        <RaffleCard key={raffle.id} raffle={raffle} onOpen={() => void navigate(`/raffles/${raffle.id}`)} />
      ))}

      {drawn.length > 0 ? <GroupCaption>Завершены</GroupCaption> : null}
      {drawn.map((raffle) => (
        <RaffleCard key={raffle.id} raffle={raffle} onOpen={() => void navigate(`/raffles/${raffle.id}`)} />
      ))}
    </Screen>
  );
}
